/**
 * Fresh bulk-upload panel mounted inside the Content Management dashboard.
 *
 * Deliberately written from scratch (no copy from Admin Command Center's
 * SyllabusBulkUpload) to sidestep the historical bugs we hit there:
 *   • uses the `apiPost` / `apiGet` wrappers so same-origin resolution works
 *     identically in preview and in production
 *   • ingests one file per request so each network round-trip stays under the
 *     production ingress's 60-second proxy-read-timeout
 *   • board / class / subject are DROPDOWNS (not free-text) so ingested rows
 *     always match the AI retrieval filters
 *   • new submissions land in status='pending_review' and only show up to
 *     students after the Admin approves them via ContentMgmtSyllabusLibrary
 */
import { useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, API_BASE, authedFetch, chunkedUpload } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText, UploadCloud, CheckCircle2, AlertTriangle, XCircle,
  Loader2, Info, Sparkles, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const BOARD_OPTIONS = [
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE / CISCE' },
  { value: 'ib', label: 'IB' },
  { value: 'igcse', label: 'Cambridge IGCSE' },
  { value: 'state_board', label: 'State Board' },
  { value: 'nios', label: 'NIOS' },
];

const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1), label: `Class ${i + 1}`,
}));

// Baseline subjects available in EVERY row's dropdown regardless of board/class.
// Per-(board,class) extensions from the DB (via /api/onboarding/subjects) and
// any admin-created subjects from this session are merged on top via
// `subjectsByScope` state below. The literal '__ADD_NEW__' is a sentinel that
// triggers the inline "+ Add new subject…" prompt.
const BASE_SUBJECTS_LIST = [
  // Core academic
  'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'Biotechnology',
  'English', 'Hindi', 'Sanskrit',
  'Social Science', 'History', 'Geography', 'Civics', 'Economics',
  'Political Science', 'Psychology', 'Sociology',
  // Commerce / Senior secondary electives
  'Accountancy', 'Business Studies', 'Computer Science', 'Informatics Practices',
  // Arts / Vocational / PE (NEP 2020 additions)
  'Arts', 'Fine Art', 'Sangeet', 'Home Science', 'Creative Writing & Translation',
  'Physical Education And Well Being', 'Health & Physical Education',
  'Vocational Education', 'Skills Education', 'The World Around Us',
  // Languages (regional)
  'Kannada', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Bengali',
  // Environmental
  'EVS/Environmental Studies',
];
const SUBJECT_OPTIONS = BASE_SUBJECTS_LIST.map(s => ({ value: s, label: s }));
const ADD_NEW_SUBJECT_SENTINEL = '__ADD_NEW__';

// Stages of a single file's lifecycle. Kept explicit (not derived) so the
// review table can render the exact stage without mental gymnastics.
const STAGE = {
  PICKED: 'picked',
  CHECKING: 'checking-duplicate',
  DUPLICATE: 'duplicate',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  CLASSIFYING: 'classifying',
  READY: 'ready',            // uploaded + classified, ready for ingest
  INGESTING: 'ingesting',
  INGESTED: 'ingested',      // submitted → pending_review on backend
  FAILED: 'failed',
};

function stageBadge(stage, dupInfo, pct) {
  switch (stage) {
    case STAGE.PICKED:
    case STAGE.CHECKING:
      return <Badge className="bg-slate-100 text-slate-600 rounded-full text-[10px]">checking…</Badge>;
    case STAGE.DUPLICATE:
      return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px] uppercase tracking-wider">Duplicate</Badge>;
    case STAGE.UPLOADING:
      return <Badge className="bg-amber-100 text-amber-700 rounded-full text-[10px]" data-testid="row-uploading">
        uploading… {typeof pct === 'number' && pct > 0 ? `${pct}%` : ''}
      </Badge>;
    case STAGE.UPLOADED:
      return <Badge className="bg-sky-100 text-sky-700 rounded-full text-[10px]">uploaded</Badge>;
    case STAGE.CLASSIFYING:
      return <Badge className="bg-violet-100 text-violet-700 rounded-full text-[10px]">auto-classifying…</Badge>;
    case STAGE.READY:
      return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ready for ingest</Badge>;
    case STAGE.INGESTING:
      return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ingesting…</Badge>;
    case STAGE.INGESTED:
      return <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px] uppercase tracking-wider">Pending review</Badge>;
    case STAGE.FAILED:
      return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px]">failed</Badge>;
    default:
      return null;
  }
}

export default function ContentMgmtSyllabusBulk({ onBatchIngested }) {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);     // array of row objects (see below)
  const [runningIngest, setRunningIngest] = useState(false);
  // Per-(board, class) subject extensions fetched lazily from the DB + any
  // subjects the admin creates inline during this session. Keyed by
  // `${board}|${class_level}`. Merged with BASE_SUBJECTS_LIST when rendering
  // each row's Subject dropdown so NEP additions (Arts, Vocational Ed, etc.)
  // appear without any hardcoded list update.
  const [subjectsByScope, setSubjectsByScope] = useState({});

  // Lazily fetch subjects for a (board, class) the first time we see it in
  // any row. Cached in `subjectsByScope` to avoid duplicate API calls.
  const ensureSubjectsForScope = async (board, classLevel) => {
    if (!board || !classLevel) return;
    const key = `${board}|${classLevel}`;
    if (subjectsByScope[key]) return;
    try {
      const r = await apiGet(`/onboarding/subjects?class_level=${encodeURIComponent(classLevel)}&board=${encodeURIComponent(board)}`);
      const names = (r.subjects || []).map(s => s.name).filter(Boolean);
      setSubjectsByScope(prev => ({ ...prev, [key]: names }));
    } catch { /* non-fatal — dropdown still has the baseline list */ }
  };

  // Merged subject list for a given row's (board, class). Always includes
  // BASE_SUBJECTS_LIST + DB-reported names + the row's current value
  // (defensive — covers detected-but-not-in-list cases). Sorted alphabetically.
  const subjectsFor = (board, classLevel, currentValue) => {
    const key = `${board}|${classLevel}`;
    const fromScope = subjectsByScope[key] || [];
    const merged = new Set([...BASE_SUBJECTS_LIST, ...fromScope]);
    if (currentValue) merged.add(currentValue);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  };

  // Row shape:
  // {
  //   id, file (File), name, sizeMb,
  //   stage, error,
  //   pdfUrl,                  — after upload
  //   duplicate (bool), dupExisting (object),
  //   detected (object), pageCount (int),
  //   board, classLevel, subject, topicName, — classified / editable
  //   submissionId              — after ingest
  // }

  const pick = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    const list = Array.from(files).filter(
      f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    if (!list.length) {
      toast.error('Only PDF files are accepted');
      return;
    }
    const now = Date.now();
    const newRows = list.map((f, i) => ({
      id: `r_${now}_${i}_${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      name: f.name,
      sizeMb: +(f.size / 1024 / 1024).toFixed(2),
      stage: STAGE.PICKED,
      error: null,
      pdfUrl: null,
      duplicate: false,
      dupExisting: null,
      detected: null,
      pageCount: 0,
      board: '',
      classLevel: '',
      subject: '',
      topicName: '',
      submissionId: null,
    }));
    setRows(prev => [...prev, ...newRows]);

    // Pre-flight duplicate check against the Content Library. Filenames that
    // already exist are flagged immediately and can't be uploaded.
    setRows(prev => prev.map(r =>
      newRows.some(n => n.id === r.id) ? { ...r, stage: STAGE.CHECKING } : r
    ));
    try {
      const res = await apiPost('/syllabus/check-duplicates', {
        filenames: newRows.map(r => r.name),
      });
      const dupMap = res.duplicates || {};
      setRows(prev => prev.map(r => {
        if (!newRows.some(n => n.id === r.id)) return r;
        const dup = dupMap[r.name];
        if (dup) {
          return { ...r, stage: STAGE.DUPLICATE, duplicate: true, dupExisting: dup };
        }
        return { ...r, stage: STAGE.PICKED };
      }));
      const dupCount = Object.keys(dupMap).length;
      if (dupCount > 0) {
        toast.error(`${dupCount} file${dupCount === 1 ? '' : 's'} already exist in the library — those will be skipped`);
      }
    } catch {
      setRows(prev => prev.map(r =>
        newRows.some(n => n.id === r.id) && r.stage === STAGE.CHECKING
          ? { ...r, stage: STAGE.PICKED } : r
      ));
    }

    // ---- INSTANT FILENAME-BASED CLASSIFICATION ----
    // Pre-fill board / class / subject / topic from the filename before the
    // user even clicks "Upload & auto-classify". Pure regex pass, no file
    // I/O, no LLM call — completes in <50ms.
    // Highest hit rate for filenames following the convention:
    //   {Board}-Class-{N}-{Subject}-{Chapter Name}.pdf
    try {
      const qc = await apiPost('/syllabus/quick-classify-names', {
        filenames: newRows.map(r => r.name),
      });
      const byName = new Map((qc.items || []).map(it => [it.filename, it.detected]));
      let hi = 0, mid = 0, low = 0;
      setRows(prev => prev.map(r => {
        if (!newRows.some(n => n.id === r.id)) return r;
        const det = byName.get(r.name);
        if (!det) return r;
        // Don't override fields admin already edited (we just picked, so they shouldn't have)
        const patched = {
          detected: det,
          board: r.board || normalizeBoard(det.board) || '',
          classLevel: r.classLevel || String(det.class_level || ''),
          subject: r.subject || det.subject || '',
          topicName: r.topicName || det.topic || '',
        };
        if (det.confidence === 'high') hi += 1;
        else if (det.confidence === 'medium') mid += 1;
        else low += 1;
        return { ...r, ...patched };
      }));
      // Warm the subject cache for every (board, class) we just detected so
      // the dropdown is pre-populated with DB-known subjects (Arts, etc.)
      // when the admin opens it — no spinner, no flicker.
      const scopes = new Set();
      (qc.items || []).forEach(it => {
        const b = normalizeBoard(it.detected?.board);
        const c = it.detected?.class_level && String(it.detected.class_level);
        if (b && c) scopes.add(`${b}|${c}`);
      });
      scopes.forEach(key => {
        const [b, c] = key.split('|');
        ensureSubjectsForScope(b, c);
      });
      const total = newRows.length;
      if (hi + mid > 0) {
        const parts = [];
        if (hi > 0) parts.push(`${hi} high-confidence`);
        if (mid > 0) parts.push(`${mid} partial`);
        if (low > 0) parts.push(`${low} needs review`);
        toast.success(`Auto-detected ${hi + mid}/${total} from filenames (${parts.join(' · ')})`);
      } else if (total > 0) {
        toast.info('Filenames didn\'t match the recommended convention — fields stay blank, fill in manually');
      }
    } catch {
      // Quick-classify is non-fatal — admin can still upload and full classify will run.
    }
  };

  const onPick = (e) => handleFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const setRow = (id, patch) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  // Inline "+ Add new subject…" handler — prompts the admin, validates,
  // POSTs to /api/admin/subjects, then injects the new name into the row +
  // the shared cache so every other row with the same (board, class) sees it.
  const addNewSubjectInline = async (row) => {
    if (!row?.board || !row?.classLevel) {
      toast.error('Pick a board and class first — new subjects are board+class specific.');
      return;
    }
    const raw = window.prompt(
      `Add a NEW subject for ${row.board.toUpperCase()} · Class ${row.classLevel}\n\n` +
      `Enter the exact subject name (e.g. "Arts", "Robotics"). ` +
      `Capitalisation and spelling will be shown to students.`,
    );
    if (raw === null) return;
    const name = raw.trim();
    if (!name) { toast.error('Subject name cannot be empty'); return; }
    if (name.length > 60) { toast.error('Subject name too long (max 60 chars)'); return; }
    try {
      const res = await apiPost('/admin/subjects', {
        name, board: row.board, class_level: row.classLevel,
      });
      const finalName = res?.subject?.name || name;
      const key = `${row.board}|${row.classLevel}`;
      setSubjectsByScope(prev => ({
        ...prev,
        [key]: Array.from(new Set([...(prev[key] || []), finalName])),
      }));
      setRow(row.id, { subject: finalName });
      toast.success(res?.created
        ? `Added "${finalName}" — available for all rows now`
        : `"${finalName}" already exists — selected it`);
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : (e?.message || 'Failed to add subject'));
    }
  };

  // Upload + classify a single row. Uses `chunkedUpload` which auto-routes:
  // <=50 MB through the existing single-POST endpoint, larger files split
  // into 5 MB chunks (bypasses CDN edge body-size limits — gurukulveda.com
  // is fronted by Cloudflare which caps single POSTs at 100 MB).
  const uploadAndClassifyOne = async (row) => {
    if (row.duplicate) return;
    try {
      setRow(row.id, { stage: STAGE.UPLOADING, error: null, uploadPct: 0 });
      const upJson = await chunkedUpload(row.file, {
        onProgress: (loaded, total) => {
          const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setRow(row.id, { uploadPct: pct });
        },
      });
      const pdfUrl = upJson.file_url;
      setRow(row.id, { stage: STAGE.CLASSIFYING, pdfUrl });

      // Classify using the existing backend endpoint
      const clsRes = await apiPost('/syllabus/bulk-classify', {
        pdf_urls: [pdfUrl],
        original_filenames: { [pdfUrl]: row.name },
        use_llm_fallback: true,
      });
      const item = (clsRes.items || [])[0] || {};
      const detected = item.detected || {};
      setRow(row.id, {
        stage: STAGE.READY,
        detected,
        pageCount: item.page_count || 0,
        board: normalizeBoard(detected.board),
        classLevel: String(detected.class_level || ''),
        subject: detected.subject || '',
        topicName: detected.topic || '',
      });
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'upload/classify failed' });
    }
  };

  const uploadAllPending = async () => {
    const pending = rows.filter(r =>
      r.stage === STAGE.PICKED && !r.duplicate
    );
    if (!pending.length) {
      toast.error('No new files to upload');
      return;
    }
    for (const r of pending) {
      await uploadAndClassifyOne(r);  // sequential — easier to reason about
    }
  };

  // Unused but kept for backwards-compat — prefer `ingestAllReady` which uses
  // the new job-tracker flow.
  const ingestOne = async (row) => {
    if (!row.board || !row.classLevel || !row.subject) {
      setRow(row.id, { stage: STAGE.FAILED, error: 'board / class / subject required' });
      return { status: 'error' };
    }
    setRow(row.id, { stage: STAGE.INGESTING, error: null });
    try {
      const res = await apiPost('/syllabus/bulk-ingest', {
        items: [{
          pdf_url: row.pdfUrl,
          board: row.board,
          class_level: row.classLevel,
          subject: row.subject,
          topic_name: row.topicName || '',
          original_filename: row.name,
        }],
      });
      // Legacy branch: if the server returned the old {outcomes: [...]} shape
      // (no longer produced), fall back to that. Otherwise the job-based path
      // is used and this function shouldn't be called.
      const outcome = (res.outcomes || [])[0] || {};
      if (outcome.status === 'ok') {
        setRow(row.id, { stage: STAGE.INGESTED, submissionId: outcome.submission_id });
        return { status: 'ok' };
      }
      setRow(row.id, { stage: STAGE.FAILED, error: outcome.error || 'ingest failed' });
      return { status: 'error' };
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'network error' });
      return { status: 'error' };
    }
  };

  /**
   * JOB-BASED INGEST (production-safe).
   *
   * Instead of holding an HTTP request open for 30+ seconds of PyMuPDF rendering
   * — which is killed by Cloudflare 520 / proxy-read-timeout / worker OOM — we
   * POST a job that returns in <500 ms and contains a job_id. We then poll the
   * job status every 2s, updating per-row UI based on the backend's live state.
   *
   * This is immune to:
   *   - Cloudflare edge 520 (HTTP request completes instantly)
   *   - K8s proxy-read-timeout (same — request returns fast)
   *   - Worker OOM mid-render (backend marks just that item as "error", keeps processing the rest)
   *   - Pod restart (backend watchdog in the GET endpoint flips stalled items to "error" after 120s)
   */
  const ingestAllReady = async () => {
    const ready = rows.filter(r => r.stage === STAGE.READY);
    if (!ready.length) { toast.error('No ready rows to ingest'); return; }
    // Validate dropdowns first — saves a round-trip
    const invalid = ready.filter(r => !r.board || !r.classLevel || !r.subject);
    if (invalid.length) {
      invalid.forEach(r => setRow(r.id, { stage: STAGE.FAILED, error: 'board / class / subject required' }));
      toast.error(`${invalid.length} row(s) missing board / class / subject`);
      return;
    }

    setRunningIngest(true);

    // Build a lookup: pdf_url → rowId so we can map outcomes back to rows.
    const byUrl = new Map();
    ready.forEach(r => {
      byUrl.set(r.pdfUrl, r.id);
      setRow(r.id, { stage: STAGE.INGESTING, error: null });
    });

    // Submit ALL files in ONE job to the new pod-lifetime worker (Phase 2.37),
    // then poll the job status every 2s. The HTTP POST returns in <500ms with
    // a job_id — no rendering work in the HTTP path. The worker (a permanent
    // asyncio task spawned at pod startup, NOT tied to this HTTP request) picks
    // up the queued job and processes items one by one. Pod death = stale-claim
    // recovery hands the job to another pod after 90s.
    //
    // This is bulletproof against:
    //   - Cloudflare/Emergent ingress 520 (HTTP request is <500ms, can't time out)
    //   - K8s proxy-read-timeout (same)
    //   - Worker OOM mid-render (job item flagged "error", processing continues)
    //   - Pod restart (job re-queued by stale-claim recovery, another pod picks up)
    //   - Network blips during polling (poll endpoint is <100ms read-only)
    let jobId;
    try {
      const res = await apiPost('/syllabus/bulk-ingest', {
        items: ready.map(r => ({
          pdf_url: r.pdfUrl,
          board: r.board,
          class_level: r.classLevel,
          subject: r.subject,
          topic_name: r.topicName || '',
          original_filename: r.name,
        })),
      });
      jobId = res.job_id;
      if (!jobId) throw new Error('No job_id returned');
    } catch (ex) {
      const reason = ex?.message === 'UNAUTHORIZED' ? 'session expired — re-login' : (ex?.message || 'queue failed');
      ready.forEach(r => setRow(r.id, { stage: STAGE.FAILED, error: reason }));
      toast.error(`Could not queue ingestion: ${reason}`);
      setRunningIngest(false);
      return;
    }

    // Poll every 2s. Backend updates `items[].status` per file as the worker
    // progresses. Items are: pending → processing → (ok|error). The job
    // overall transitions queued → running → completed when all items reach
    // terminal states.
    const startedAt = Date.now();
    const MAX_WALL_MS = 30 * 60 * 1000;  // 30 min ceiling (huge batches OK)
    let okCount = 0;
    let errCount = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise(res => setTimeout(res, 2000));
      if (Date.now() - startedAt > MAX_WALL_MS) {
        ready.forEach(r => {
          if (r.stage === STAGE.INGESTING) {
            setRow(r.id, { stage: STAGE.FAILED, error: 'client poll timeout 30min' });
          }
        });
        toast.error('Polling exceeded 30 minutes — refresh the library to see final state');
        break;
      }
      let job;
      try {
        job = await apiGet(`/syllabus/ingest-job/${jobId}`);
      } catch {
        continue;  // transient — keep polling
      }
      (job.items || []).forEach(it => {
        const rowId = byUrl.get(it.pdf_url);
        if (!rowId) return;
        if (it.status === 'ok') {
          setRow(rowId, { stage: STAGE.INGESTED, submissionId: it.submission_id, error: null });
        } else if (it.status === 'error') {
          setRow(rowId, { stage: STAGE.FAILED, error: it.error || 'ingest failed' });
        }
      });
      if (job.all_terminal) {
        const summary = job.summary || {};
        okCount = summary.ok || 0;
        errCount = summary.error || 0;
        break;
      }
    }

    if (errCount === 0) toast.success(`All ${okCount} submitted for admin review`);
    else if (okCount === 0) toast.error(`Ingestion failed for all ${errCount} files`);
    else toast.warning(`Submitted ${okCount}/${okCount + errCount} — ${errCount} failed (hover row for details)`);
    if (okCount > 0 && onBatchIngested) onBatchIngested();

    setRunningIngest(false);
  };

  /**
   * Retry a single failed row.
   *
   * Two distinct recovery paths depending on which phase failed:
   *   1. Upload/classify failed (no pdfUrl yet) → re-run the full upload+classify
   *      pipeline. The original File handle is still in row.file so we don't
   *      need to ask the admin to re-pick it.
   *   2. Ingest failed (pdfUrl exists) → submit a 1-item job to the
   *      pod-lifetime worker and poll. Same bulletproof path as the bulk flow,
   *      just scoped to one file.
   *
   * Per-row Retry is intentionally per-file (not a bulk button), so transient
   * issues on one PDF don't force the admin to re-confirm 50 rows.
   */
  const retryRow = async (row) => {
    if (row.duplicate) return 'skip';
    // Phase 1 recovery — never got past upload/classify
    if (!row.pdfUrl) {
      await uploadAndClassifyOne(row);
      // uploadAndClassifyOne lands the row in READY (success) or FAILED.
      // READY isn't terminal "success" for the full ingest pipeline — it
      // still needs the admin to click Submit — but it's a successful retry
      // of the upload phase. We return 'ok' here so the toast reflects that
      // the retry itself worked.
      return 'ok-partial';
    }
    // Phase 2 recovery — file is uploaded, ingest failed. Validate scope first.
    if (!row.board || !row.classLevel || !row.subject) {
      setRow(row.id, { stage: STAGE.FAILED, error: 'board / class / subject required' });
      toast.error('Pick board / class / subject before retrying');
      return 'error';
    }
    setRow(row.id, { stage: STAGE.INGESTING, error: null });
    let jobId;
    try {
      const res = await apiPost('/syllabus/bulk-ingest', {
        items: [{
          pdf_url: row.pdfUrl,
          board: row.board,
          class_level: row.classLevel,
          subject: row.subject,
          topic_name: row.topicName || '',
          original_filename: row.name,
        }],
      });
      jobId = res.job_id;
      if (!jobId) throw new Error('No job_id returned');
    } catch (ex) {
      const reason = ex?.message === 'UNAUTHORIZED' ? 'session expired — re-login' : (ex?.message || 'queue failed');
      setRow(row.id, { stage: STAGE.FAILED, error: reason });
      return 'error';
    }
    // Poll the single-item job until terminal. 30-min wall mirrors the
    // primary polling loop, so a retry of even a 500 MB+ image-heavy PDF
    // (backend ceiling 15 min × 2 attempts) gets full backend coverage.
    const startedAt = Date.now();
    const MAX_WALL_MS = 30 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise(res => setTimeout(res, 2000));
      if (Date.now() - startedAt > MAX_WALL_MS) {
        setRow(row.id, { stage: STAGE.FAILED, error: 'retry poll timeout 30min' });
        return 'error';
      }
      let job;
      try { job = await apiGet(`/syllabus/ingest-job/${jobId}`); }
      catch { continue; }
      const it = (job.items || []).find(x => x.pdf_url === row.pdfUrl) || (job.items || [])[0];
      if (it?.status === 'ok') {
        setRow(row.id, { stage: STAGE.INGESTED, submissionId: it.submission_id, error: null });
        if (onBatchIngested) onBatchIngested();
        return 'ok';
      }
      if (it?.status === 'error') {
        setRow(row.id, { stage: STAGE.FAILED, error: it.error || 'ingest failed' });
        return 'error';
      }
      if (job.all_terminal) return 'error';
    }
  };

  const retryAllFailed = async () => {
    const failed = rows.filter(r => r.stage === STAGE.FAILED);
    if (!failed.length) { toast.error('No failed rows to retry'); return; }
    setRunningIngest(true);
    let ok = 0, partial = 0, err = 0;
    for (const r of failed) {
      // eslint-disable-next-line no-await-in-loop
      const result = await retryRow(r);
      if (result === 'ok') ok++;
      else if (result === 'ok-partial') partial++;
      else if (result === 'error') err++;
    }
    setRunningIngest(false);
    const total = ok + partial + err;
    if (err === 0 && partial === 0) toast.success(`Retried ${ok} item(s) successfully`);
    else if (ok + partial === 0) toast.error(`All ${total} retry attempt(s) failed`);
    else {
      const parts = [];
      if (ok > 0) parts.push(`${ok} ingested`);
      if (partial > 0) parts.push(`${partial} re-classified (click Submit to ingest)`);
      if (err > 0) parts.push(`${err} still failing`);
      toast.warning(`Retried ${total}: ${parts.join(' · ')}`);
    }
  };

  const counts = useMemo(() => ({
    total: rows.length,
    dup: rows.filter(r => r.duplicate).length,
    pending: rows.filter(r => r.stage === STAGE.PICKED && !r.duplicate).length,
    ready: rows.filter(r => r.stage === STAGE.READY).length,
    ingested: rows.filter(r => r.stage === STAGE.INGESTED).length,
    failed: rows.filter(r => r.stage === STAGE.FAILED).length,
  }), [rows]);

  return (
    <div className="space-y-5" data-testid="cm-syllabus-bulk">
      <Card className="p-5 rounded-2xl border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <UploadCloud className="w-4 h-4 text-emerald-500" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Bulk upload syllabus PDFs</h3>
        </div>
        <p className="text-[11px] text-slate-500 font-body mb-4">
          Drop one or more PDFs. Each is auto-classified into (board · class · subject · chapter).
          Every submission lands in <strong>Pending Review</strong> and is only used by Gurukul Veda after an admin approves it in the Content Library below.
        </p>

        <div
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-8 text-center cursor-pointer hover:bg-slate-50"
          onClick={pick}
          data-testid="cm-syllabus-dropzone"
        >
          <UploadCloud className="w-9 h-9 mx-auto text-slate-400 mb-2" />
          <p className="font-heading text-sm text-slate-700">Drop PDFs here or click to choose files</p>
          <p className="text-[10px] text-slate-400 font-body mt-1">Duplicate filenames are detected up-front and blocked.</p>
          <input
            ref={inputRef} type="file" accept="application/pdf" multiple
            onChange={onPick} className="hidden"
            data-testid="cm-syllabus-file-input"
          />
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-heading uppercase tracking-wider">
              <Badge className="bg-slate-100 text-slate-700 rounded-full">Total: {counts.total}</Badge>
              {counts.dup > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Duplicates: {counts.dup}</Badge>}
              {counts.pending > 0 && <Badge className="bg-amber-100 text-amber-700 rounded-full">To upload: {counts.pending}</Badge>}
              {counts.ready > 0 && <Badge className="bg-indigo-100 text-indigo-700 rounded-full">Ready: {counts.ready}</Badge>}
              {counts.ingested > 0 && <Badge className="bg-emerald-100 text-emerald-700 rounded-full">Pending review: {counts.ingested}</Badge>}
              {counts.failed > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Failed: {counts.failed}</Badge>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={uploadAllPending}
                disabled={counts.pending === 0 || runningIngest}
                data-testid="cm-syllabus-upload-all-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full text-xs font-heading disabled:opacity-40"
              >
                Upload &amp; auto-classify ({counts.pending})
              </Button>
              <Button
                onClick={ingestAllReady}
                disabled={counts.ready === 0 || runningIngest}
                data-testid="cm-syllabus-ingest-all-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-heading disabled:opacity-40"
              >
                {runningIngest ? `Ingesting ${counts.ingested + counts.failed + 1}/${counts.ready + counts.ingested + counts.failed}…` : `Submit ${counts.ready} for review`}
              </Button>
              {counts.failed > 0 && (
                <Button
                  onClick={retryAllFailed}
                  disabled={runningIngest}
                  data-testid="cm-syllabus-retry-all-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading disabled:opacity-40"
                >
                  <RotateCcw className="w-3 h-3 mr-1.5" />
                  Retry {counts.failed} failed
                </Button>
              )}
              <Button
                onClick={() => setRows([])}
                disabled={runningIngest}
                variant="ghost"
                className="text-slate-500 text-xs font-heading disabled:opacity-40"
                data-testid="cm-syllabus-clear-btn"
              >
                Clear list
              </Button>
            </div>
          </>
        )}
      </Card>

      {rows.length > 0 && (
        <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="cm-syllabus-rows">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Filename</th>
                  <th className="text-left px-3 py-2">Board</th>
                  <th className="text-left px-3 py-2">Class</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Chapter</th>
                  <th className="text-right px-3 py-2">Pages</th>
                  <th className="text-center px-3 py-2">Stage</th>
                  <th className="text-center px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${r.duplicate ? 'bg-rose-50/50' : r.stage === STAGE.FAILED ? 'bg-rose-50/30' : r.stage === STAGE.INGESTED ? 'bg-emerald-50/30' : ''}`}
                      data-testid={`cm-row-${idx}`}>
                    <td className="px-3 py-2 max-w-[320px]">
                      <p className="font-body text-slate-800 break-all leading-snug" title={r.name}>{r.name}</p>
                      <p className="text-[10px] text-slate-400 font-body mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{r.sizeMb} MB</span>
                        {r.detected && r.detected.confidence === 'high' && (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600" title="Auto-detected board, class, subject and topic from filename">
                            <Sparkles className="w-2.5 h-2.5" /> auto-detected
                          </span>
                        )}
                        {r.detected && r.detected.confidence === 'medium' && (
                          <span className="inline-flex items-center gap-0.5 text-amber-600" title="Some fields auto-detected; please verify the others">
                            <Sparkles className="w-2.5 h-2.5" /> partial — verify
                          </span>
                        )}
                      </p>
                      {r.duplicate && r.dupExisting && (
                        <p className="text-[10px] text-rose-600 font-body mt-1" data-testid={`cm-dup-detail-${idx}`}>
                          Uploaded on {r.dupExisting.created_at?.slice(0, 10) || '—'} ·
                          {r.dupExisting.board ? ` ${String(r.dupExisting.board).toUpperCase()}` : ''}
                          {r.dupExisting.class_level ? ` · Class ${r.dupExisting.class_level}` : ''}
                          {r.dupExisting.subject ? ` · ${r.dupExisting.subject}` : ''}
                        </p>
                      )}
                      {r.error && (
                        <p className="text-[10px] text-rose-600 font-body mt-1" title={r.error}>
                          <AlertTriangle className="inline w-3 h-3 mr-0.5" />{r.error}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[120px]">
                      <select
                        disabled={lockInputs(r)}
                        value={r.board}
                        onChange={e => setRow(r.id, { board: e.target.value })}
                        data-testid={`cm-row-board-${idx}`}
                        className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50"
                      >
                        <option value="">—</option>
                        {BOARD_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[100px]">
                      <select
                        disabled={lockInputs(r)}
                        value={r.classLevel}
                        onChange={e => setRow(r.id, { classLevel: e.target.value })}
                        data-testid={`cm-row-class-${idx}`}
                        className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50"
                      >
                        <option value="">—</option>
                        {CLASS_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[160px]">
                      <select
                        disabled={lockInputs(r)}
                        value={r.subject}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === ADD_NEW_SUBJECT_SENTINEL) {
                            addNewSubjectInline(r);
                            return;
                          }
                          setRow(r.id, { subject: val });
                        }}
                        onFocus={() => ensureSubjectsForScope(r.board, r.classLevel)}
                        data-testid={`cm-row-subject-${idx}`}
                        className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50"
                      >
                        <option value="">—</option>
                        {subjectsFor(r.board, r.classLevel, r.subject).map(s => <option key={s} value={s}>{s}</option>)}
                        <option value={ADD_NEW_SUBJECT_SENTINEL} className="italic text-indigo-600">+ Add new subject…</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[200px]">
                      <input
                        disabled={lockInputs(r)}
                        type="text"
                        value={r.topicName}
                        onChange={e => setRow(r.id, { topicName: e.target.value })}
                        placeholder="chapter / topic (optional)"
                        data-testid={`cm-row-topic-${idx}`}
                        className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50"
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600 font-body">
                      {r.pageCount || (r.stage === STAGE.READY ? '?' : '—')}
                    </td>
                    <td className="px-3 py-2 text-center" data-testid={`cm-row-stage-${idx}`}>
                      <div className="flex items-center justify-center gap-1.5">
                        {r.stage === STAGE.INGESTED && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        {r.stage === STAGE.FAILED && <AlertTriangle className="w-4 h-4 text-rose-500" />}
                        {(r.stage === STAGE.UPLOADING || r.stage === STAGE.CLASSIFYING || r.stage === STAGE.INGESTING || r.stage === STAGE.CHECKING) && (
                          <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                        )}
                        {stageBadge(r.stage, r.dupExisting, r.uploadPct)}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {r.stage === STAGE.FAILED && (
                          <button
                            onClick={() => retryRow(r)}
                            disabled={runningIngest}
                            className="text-amber-500 hover:text-amber-600 disabled:opacity-40"
                            title="Retry this file"
                            data-testid={`cm-row-retry-${idx}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => removeRow(r.id)}
                          disabled={runningIngest}
                          className="text-slate-400 hover:text-rose-500 text-xs"
                          title="Remove from list"
                          data-testid={`cm-row-remove-${idx}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-500 font-body">
            <Info className="w-3 h-3" />
            Files are ingested one at a time with an <strong>adaptive timeout</strong> (90 s base + ~1 s per MB, ceiling 15 minutes per file, plus one automatic retry on timeout). Submissions land in <strong>Pending Review</strong> — approve them in the Syllabus Library tab for Gurukul Veda to start using them.
          </div>
        </Card>
      )}
    </div>
  );
}

// --- helpers --------------------------------------------------------------

function lockInputs(row) {
  return row.stage === STAGE.UPLOADING
    || row.stage === STAGE.CLASSIFYING
    || row.stage === STAGE.INGESTING
    || row.stage === STAGE.INGESTED
    || row.stage === STAGE.DUPLICATE
    || row.stage === STAGE.CHECKING;
}

function normalizeBoard(raw) {
  if (!raw) return '';
  const s = String(raw).toLowerCase();
  if (s.includes('cbse') || s.includes('ncert')) return 'cbse';
  if (s.includes('icse') || s.includes('cisce')) return 'icse';
  if (s.includes('igcse') || s.includes('cambridge')) return 'igcse';
  if (s.includes('ib')) return 'ib';
  if (s.includes('nios')) return 'nios';
  if (s.includes('state')) return 'state_board';
  return '';
}
